import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { LanguageContext } from 'containers/LanguageProvider';

import { Grid } from 'components/Layout';
import { TransitionGroup } from 'components/Transition';
import { VisuallyHidden, LiveAnnouncer } from 'components/Utilities';

import { useId } from 'utilsTS/hooks';

import styles from './Snackbar.module.scss';

export interface SnackbarProps extends React.ComponentPropsWithoutRef<'section'> {
    /** Defines the id */
    id?: string;
    /** Accepts SnackbarItem components to be rendered */
    children?: React.ReactNode;
}

export const Snackbar: React.FC<SnackbarProps> = ({
    id: defaultId = '',
    children,
    className,
    ...rest
}) => {
    const id = useId('snackbar', defaultId);
    const { getString } = useContext(LanguageContext);
    const title = getString('page.notifications.title') as string;
    const noMessages = getString('page.notifications.empty') as string;

    return (
        <section {...rest} className={cx(styles.group, className)} aria-labelledby={id}>
            {/* this forces the aria-live areas to be rendered from the start to fix first item not announced */}
            <LiveAnnouncer message="" />
            <VisuallyHidden id={id} tag="h2" className={styles.empty}>
                {title}
            </VisuallyHidden>
            <Grid className={cx(styles.grid)} fluid={false}>
                <TransitionGroup component={null}>
                    {React.Children.toArray(children)}
                </TransitionGroup>
            </Grid>
            <VisuallyHidden tag="p" className={styles.empty}>
                {noMessages}
            </VisuallyHidden>
        </section>
    );
};

Snackbar.displayName = 'Snackbar';
Snackbar.propTypes = {
    id: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
};
